import { useMemo } from 'react'

import type { InventoryPositionItem } from '@/lib/api/report-api'

import { Button } from '@/components/ui/button'
import { TableCell, TableRow } from '@/components/ui/table'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/** Khoá của nhóm lô chưa gán vị trí. Tiền tố `code:` cho vị trí thật để mã do
 *  người dùng tự đặt (`warehouse_positions.code` là VARCHAR tự nhập) không bao
 *  giờ đụng khoá này — trùng là hai dòng bị gộp làm một và mất hẳn một nhãn. */
const UNASSIGNED_KEY = 'unassigned'

/** Một vị trí kho và tổng tồn của sản phẩm đang đứng ở đó. */
export interface PositionStock {
    /** Khoá React ổn định; mọi lô chưa gán vị trí gom về một dòng. */
    key: string
    /** Nhãn hiển thị: TÊN vị trí ("Kệ Trưng Bày"). Mã kho ("KE TRUNG BAY") chỉ
     *  là khoá kỹ thuật, người đọc báo cáo không cần thấy. */
    label: string
    /** Lô chưa gán vị trí — render nhạt đi vì đây là phần dư, không phải một kệ. */
    unassigned: boolean
    quantity: number
}

/** Nhãn hiển thị của một vị trí: tên trước, rơi về mã khi vị trí chưa đặt tên
 *  (có nhãn kỹ thuật vẫn hơn một ô trống). */
function labelOf(item: InventoryPositionItem): string {
    if (item.position_name?.trim()) return item.position_name
    if (item.position_code?.trim()) return item.position_code
    return m.product_stock_no_position()
}

/**
 * Chuẩn hóa dòng tồn theo vị trí của API thành dòng để render.
 *
 * API đã gộp sẵn theo vị trí (mọi kho, mọi mức chiết khấu) nên ở đây chỉ còn
 * việc chốt nhãn và khoá React — thứ tự giữ nguyên như server trả về. Vẫn gộp
 * phòng thân: một vị trí xuất hiện hai lần thì phải cộng lại chứ không đè lên nhau.
 *
 * Lô chưa gán vị trí gom về một dòng chứ không bỏ đi: tổng các dòng ở đây phải
 * đúng bằng cột "Tồn kho" của sản phẩm, nếu không bung dòng ra là thấy số đá nhau.
 */
export function groupByPosition(
    items: InventoryPositionItem[],
): PositionStock[] {
    const byPosition = new Map<string, PositionStock>()
    for (const item of items) {
        const key =
            item.position_code === null
                ? UNASSIGNED_KEY
                : `code:${item.position_code}`
        const found = byPosition.get(key)
        if (found) {
            found.quantity += item.quantity
            continue
        }
        byPosition.set(key, {
            key,
            label: labelOf(item),
            unassigned: item.position_code === null,
            quantity: item.quantity,
        })
    }
    // KHÔNG sắp lại: server đã sắp theo nhãn bằng collation tiếng Việt và đẩy
    // dòng "chưa gán" xuống cuối. Sắp thêm một lần nữa ở đây là mở đường cho màn
    // và file Excel nói hai thứ tự khác nhau trên cùng một sản phẩm.
    return [...byPosition.values()]
}

interface Props {
    items?: InventoryPositionItem[]
    isLoading?: boolean
    /** Lỗi tải phải nói là lỗi. Trả về nhánh rỗng khi API hỏng là khẳng định sản
     *  phẩm không còn lô nào — bịa dữ liệu, không phải trạng thái trống. */
    isError?: boolean
    onRetry?: () => void
    /** Số cột của bảng cha — dòng trạng thái (tải/lỗi/rỗng) trải hết chiều ngang. */
    colSpan: number
}

/** Ô trống giữ đúng số cột của bảng cha, để cột "Tồn" của dòng vị trí nằm thẳng
 *  hàng với cột "Tồn kho" của dòng sản phẩm. */
const FILLER_KEYS = ['min', 'cost', 'value', 'status'] as const

/** Dòng trạng thái (đang tải / lỗi / rỗng) trải hết bảng. */
function StatusRow({
    colSpan,
    children,
}: {
    colSpan: number
    children: React.ReactNode
}) {
    return (
        <TableRow className="bg-muted/40 hover:bg-muted/40">
            <TableCell colSpan={colSpan} className="py-3 pl-10">
                {children}
            </TableCell>
        </TableRow>
    )
}

/**
 * Các dòng bung ra dưới một dòng sản phẩm của báo cáo tồn kho: hàng đó đang nằm
 * ở những vị trí nào, mỗi vị trí bao nhiêu.
 *
 * Render bằng chính cột của bảng cha (không lồng bảng con) để số tồn của từng vị
 * trí nằm thẳng hàng với cột "Tồn kho" của sản phẩm — mắt đọc dọc một cột là
 * cộng được, không phải nhảy sang bảng khác.
 */
export function InventoryPositionRows({
    items,
    isLoading,
    isError,
    onRetry,
    colSpan,
}: Props) {
    const positions = useMemo(() => groupByPosition(items ?? []), [items])

    // Bốn trạng thái khác hẳn nhau: đang tải, lỗi, đã tải mà không còn lô nào,
    // và có dữ liệu. Gộp bất kỳ hai cái nào thành "không có gì" là nói dối.
    if (isLoading) {
        return (
            <StatusRow colSpan={colSpan}>
                <span className="text-sm text-muted-foreground">
                    {m.common_loading()}
                </span>
            </StatusRow>
        )
    }
    if (isError) {
        return (
            <StatusRow colSpan={colSpan}>
                <div className="flex flex-wrap items-center gap-3">
                    <span role="alert" className="text-sm text-destructive">
                        {m.report_inventory_positions_error()}
                    </span>
                    {onRetry ? (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={onRetry}
                        >
                            {m.report_inventory_positions_retry()}
                        </Button>
                    ) : null}
                </div>
            </StatusRow>
        )
    }
    if (positions.length === 0) {
        return (
            <StatusRow colSpan={colSpan}>
                <span className="text-sm text-muted-foreground">
                    {m.product_stock_empty()}
                </span>
            </StatusRow>
        )
    }

    return (
        <>
            {positions.map((pos) => (
                <TableRow
                    key={pos.key}
                    className="bg-muted/40 hover:bg-muted/40"
                >
                    <TableCell className="py-1.5 pl-10 text-sm">
                        {/* Dòng này dùng chung cột với bảng cha nên tên vị trí
                            nằm dưới header "Sản phẩm": không có nhãn ẩn thì
                            trình đọc màn hình đọc "Kệ A" như một tên sản phẩm. */}
                        <span className="sr-only">
                            {m.report_inventory_position_sr_label()}{' '}
                        </span>
                        <span
                            className={
                                pos.unassigned
                                    ? 'text-muted-foreground italic'
                                    : 'text-muted-foreground'
                            }
                        >
                            {pos.label}
                        </span>
                    </TableCell>
                    <TableCell />
                    <TableCell className="py-1.5 text-right text-sm tabular-nums text-muted-foreground">
                        <span className="sr-only">
                            {m.report_inventory_quantity_sr_label()}{' '}
                        </span>
                        {formatQty(pos.quantity)}
                    </TableCell>
                    {FILLER_KEYS.map((key) => (
                        <TableCell key={key} />
                    ))}
                </TableRow>
            ))}
        </>
    )
}
